import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { developers } from '../utils/data';
import { CalendarIcon, MapPinIcon, GlobeIcon, TwitterIcon, LinkedinIcon, GithubIcon } from 'lucide-react';
const DeveloperProfile = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const developer = developers.find(dev => dev.id === id);
  if (!developer) {
    return <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-586">
        <div className="max-w-3xl mx-auto text-center" data-id="element-587">
          <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-588">
            Developer Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-589">
            We couldn't find the developer you're looking for.
          </p>
          <Link to="/developers" data-id="element-590">
            <Button variant="primary" data-id="element-591">Back to Developers</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-592">
      <div className="max-w-4xl mx-auto" data-id="element-593">
        {/* Profile header */}
        <div className="bg-white border border-[#FF3B30] rounded-lg shadow-sm p-6 mb-6" data-id="element-594">
          <div className="flex flex-col sm:flex-row items-center sm:items-start" data-id="element-595">
            <img src={developer.avatar} alt={developer.name} className="w-24 h-24 rounded-full object-cover border-2 border-[#FF3B30] shadow-sm" data-id="element-596" />
            <div className="sm:ml-6 mt-4 sm:mt-0 text-center sm:text-left flex-1" data-id="element-597">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between" data-id="element-598">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-599">
                  {developer.name}
                </h1>
                <div className={`mt-2 sm:mt-0 px-3 py-1 text-sm font-medium rounded-full inline-flex items-center ${developer.status === 'available' ? 'bg-green-100 text-green-800' : developer.status === 'looking' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-600">
                  <span className={`w-2 h-2 rounded-full mr-1.5 ${developer.status === 'available' ? 'bg-green-500' : developer.status === 'looking' ? 'bg-blue-500' : 'bg-gray-500'}`} data-id="element-601"></span>
                  {developer.status.charAt(0).toUpperCase() + developer.status.slice(1)}
                </div>
              </div>
              <p className="text-lg text-gray-600 mt-1" data-id="element-602">{developer.title}</p>
              <div className="flex flex-col sm:flex-row sm:items-center mt-4 text-sm text-gray-500 space-y-2 sm:space-y-0 sm:space-x-4" data-id="element-603">
                <div className="flex items-center justify-center sm:justify-start" data-id="element-604">
                  <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-605" />
                  {developer.location} {getFlagEmoji(developer.country)}
                </div>
                <div className="flex items-center justify-center sm:justify-start" data-id="element-606">
                  <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-607" />
                  Approved{' '}
                  {new Date(developer.approvalDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-608">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6" data-id="element-609">
            {/* Bio */}
            <Card data-id="element-610">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-611">
                About
              </h2>
              <p className="text-gray-700" data-id="element-612">{developer.bio}</p>
            </Card>
            {/* Skills */}
            <Card data-id="element-613">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-614">
                Skills
              </h2>
              <div className="flex flex-wrap gap-2" data-id="element-615">
                {developer.skills.map(skill => <span key={skill.name} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-616">
                    {skill.name}
                  </span>)}
              </div>
            </Card>
          </div>
          {/* Right column */}
          <div className="space-y-6" data-id="element-617">
            {/* Contact/Availability */}
            <Card data-id="element-618">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-619">
                Availability
              </h2>
              <div className="mb-4" data-id="element-620">
                <div className="text-gray-700 font-medium" data-id="element-621">Rate</div>
                <div className="text-xl font-semibold text-gray-900 mt-1" data-id="element-622">
                  ${developer.rate.min}-${developer.rate.max}{' '}
                  <span className="text-sm font-normal text-gray-500" data-id="element-623">
                    / hour
                  </span>
                </div>
              </div>
              <div className="mb-4" data-id="element-624">
                <div className="text-gray-700 font-medium" data-id="element-625">Open to</div>
                <div className="flex flex-wrap gap-2 mt-1" data-id="element-626">
                  {developer.open_to.map(item => <span key={item} className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm" data-id="element-627">
                      {item}
                    </span>)}
                </div>
              </div>
              <Button variant="primary" fullWidth data-id="element-628">
                Contact Developer
              </Button>
            </Card>
            {/* Links */}
            <Card data-id="element-629">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-630">
                Links
              </h2>
              <div className="space-y-3" data-id="element-631">
                {developer.links.website && <a href={developer.links.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#FF3B30] transition-colors" data-id="element-632">
                    <GlobeIcon className="h-5 w-5 mr-2" data-id="element-633" />
                    Website
                  </a>}
                {developer.links.github && <a href={developer.links.github} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#FF3B30] transition-colors" data-id="element-634">
                    <GithubIcon className="h-5 w-5 mr-2" data-id="element-635" />
                    GitHub
                  </a>}
                {developer.links.twitter && <a href={developer.links.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#FF3B30] transition-colors" data-id="element-636">
                    <TwitterIcon className="h-5 w-5 mr-2" data-id="element-637" />
                    Twitter
                  </a>}
                {developer.links.linkedin && <a href={developer.links.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#FF3B30] transition-colors" data-id="element-638">
                    <LinkedinIcon className="h-5 w-5 mr-2" data-id="element-639" />
                    LinkedIn
                  </a>}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>;
};
export default DeveloperProfile;
// Helper function to get flag emoji
const getFlagEmoji = (country: string) => {
  const countryFlags: {
    [key: string]: string;
  } = {
    'United States': '🇺🇸',
    Uganda: '🇺🇬',
    Germany: '🇩🇪',
    Philippines: '🇵🇭',
    Egypt: '🇪🇬',
    Italy: '🇮🇹',
    Romania: '🇷🇴',
    France: '🇫🇷',
    Spain: '🇪🇸',
    Nigeria: '🇳🇬',
    Pakistan: '🇵🇰',
    'United Kingdom': '🇬🇧',
    Switzerland: '🇨🇭',
    Indonesia: '🇮🇩',
    Turkey: '🇹🇷'
  };
  return countryFlags[country] || '🏳️';
};